import React, {useState, useEffect} from 'react';
import styled from 'styled-components';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIosNew';

const NumberInputContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 4px;

  height: 38px;
  width: 100px;
  border-radius: 4px;
  border: 1px rgba(32, 33, 36, 0.1) solid;
  color: rgba(32, 33, 36, 0.6);

  & > svg {
    cursor: pointer;
    &:hover,
    &:focus {
      color: black;
    }
  }
`;

const NumInput = styled.input.attrs({type: 'number'})`
  height: 32px;
  text-align: center;
  border: none;
  width: 48px;
  font-size: 16px;
  color: black;

  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
  }

  &:focus,
  &:active {
    border: none;
    outline: none;
  }
`;

export function NumberInput({
  value,
  onChange,
  min = 1,
  max,
  step = 1,
}: {
  value: number;
  onChange: (val: number) => void;
  min?: number;
  max: number;
  step?: number;
}) {
  const [val, setVal] = useState<number | string>(value);

  useEffect(() => {
    setVal(value);
  }, [value]);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setVal(e.target.value);

    const num = parseInt(e.target.value);
    if (!!num && num >= min && num <= max) onChange(num);
    if (num < min || num > max) setVal(value);
  }

  function inc() {
    if (!val) {
      setVal(min);
      return onChange(min);
    }
    const num = typeof val === 'string' ? parseInt(val) : val;
    if (isNaN(num)) {
      setVal(min);
      return onChange(min);
    }
    const lastStep = num - (num % step);
    onChange(Math.min(lastStep + step, max));
  }

  function dec() {
    if (!val) {
      setVal(min);
      return onChange(min);
    }
    const num = typeof val === 'string' ? parseInt(val) : val;
    if (isNaN(num)) {
      setVal(min);
      return onChange(min);
    }
    const lastStep = num - (num % step);
    onChange(Math.max(min, lastStep === num ? lastStep - step : lastStep));
  }

  return (
    <NumberInputContainer>
      <ArrowBackIosIcon
        role="button"
        tabIndex={0}
        aria-label="Decrease number"
        fontSize="small"
        onClick={dec}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            dec();
          }
        }}
      />
      <NumInput value={val} min={min} max={max} onChange={handleChange} />
      <ArrowForwardIosIcon
        role="button"
        tabIndex={0}
        aria-label="Increase number"
        fontSize="small"
        onClick={inc}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            inc();
          }
        }}
      />
    </NumberInputContainer>
  );
}
